// ===========================================================================
// The account panel: sign in, create an account, or sign out.
//
// Signing in used to be reachable only through the Lumi Plus modal, so somebody
// who just wanted their own name back had to open a price list and read past
// subscriptions, cosmetics, and room upgrades to find the form. An account is
// optional on Lumi and has nothing to do with paying, so it gets its own door.
// The paid extras stay one click away for anyone who does want them.
//
// The same door serves a KEPT IDENTITY: a keepsake the person has put a name
// and a password on (server keepsake-auth.js). A teen, who cannot have an
// account, signs in here to the keepsake they named on another device; an
// adult who named theirs does the same, and the form does not make them say
// which. "Keep this identity" lives in the This-device block below, because
// that is what it is: the device key, with a name on it. ASCII only.
// ===========================================================================

import { t } from '../i18n.js';
import { asModal } from './modal.js';
import { el, toast } from '../dom.js';
import { SERVER_HTTP } from '../config.js';
import { getKeepsake, forgetKeepsake, keepsakeMe, nameKeepsake } from '../keepsake.js';
import { renderAuth } from './auth.js';
import { renderRecoveryCode } from './recovery-code.js';
import { renderDevices, accountDevicesApi, keepsakeDevicesApi } from './devices.js';
import { getFingerprint } from '../device-signal.js';

/**
 * Open the account panel.
 * @param {import('../account.js').AccountClient} account
 * @param {{ onChange?: (account: object|null) => void, onKeepsake?: (keepsake: object|null) => void, onPlus?: () => void, mode?: 'login'|'register', band?: 'minor'|'adult'|null, keep?: boolean }} [opts]
 *   `keep` opens with the "keep this identity" form already unfolded: the regulars panel's nudge lands here.
 *   `onKeepsake` is told when this device's keepsake was named, signed in to,
 *   or forgotten: who the page is may have changed without an account moving.
 */
export function openAccount(account, opts = {}) {
  const onChange = opts.onChange || (() => {});
  const onKeepsake = opts.onKeepsake || (() => {});
  const band = opts.band === 'minor' ? 'minor' : opts.band === 'adult' ? 'adult' : null;
  const body = el('div', { class: 'billing-body' });

  const modal = el('div', { class: 'billing-modal account-modal' }, [
    el('div', { class: 'billing-head' }, [
      el('strong', { text: t('Your Lumi account') }),
      el('button', { class: 'btn btn-ghost btn-sm', text: t('Close'), onClick: () => dialog.close() }),
    ]),
    body,
    el('div', {
      class: 'billing-foot muted',
      text: t('An account is optional. Lumi works without one. Signing in only remembers your name, your Plus membership, and anything you have unlocked.'),
    }),
  ]);
  document.body.append(modal);
  // A dialog behaves like one: a backdrop that dims and blocks the page,
  // role and aria-modal, focus moved in and trapped, Escape to leave, and
  // focus put back where it came from. See ui/modal.js.
  const dialog = asModal(modal, { label: 'Your Lumi account' });

  // What the server remembers for this device's key, once asked: null until
  // then, and a record with `username: null` for a keepsake nobody named.
  // Asked when the panel opens and again after naming, signing in, or
  // forgetting. Only the device block is redrawn unless the NAME changed,
  // because a full redraw would throw away a half-typed sign-in.
  let me = null;
  let drawnName = null;
  const deviceBox = el('div', {});
  const keptName = () => (me && me.username) || null;
  function askMe() {
    keepsakeMe({ base: SERVER_HTTP })
      .then((m) => {
        me = m;
        if (keptName() !== drawnName) render();
        else renderDevice();
      })
      .catch(() => {});
  }

  // What Lumi remembers on THIS DEVICE, for everyone -- with or without an
  // account -- and the way out next to it. Said plainly: the whole point of a
  // keepsake is that it holds nothing a person would need to be told about,
  // and the honest way to prove that is to say exactly what it is.
  function keepsakeBlock() {
    const has = !!getKeepsake();
    const name = keptName();
    const forgetBtn = el('button', {
      class: 'btn btn-ghost btn-sm',
      text: t('Forget me on this device'),
      onClick: async () => {
        // Irreversible, and one tap below "Keep it": on 2026-09-29 a
        // 13-17's regulars and messages went this way, most likely by a
        // mis-tap. Said in full before anything is done.
        if (!window.confirm(name
          ? `Forget this device? Your regulars, waves and messages are deleted for good, and the name @${name} and its password go with them. There is no way back.`
          : 'Forget this device? Your regulars, waves and messages made on this key are deleted for good. There is no way back.')) return;
        forgetBtn.disabled = true;
        const confirmed = await forgetKeepsake({ base: SERVER_HTTP, fingerprint: await getFingerprint().catch(() => '') });
        if (confirmed === 'banned') {
          // Refused, and nothing cleared: the identity waits out the ban.
          toast('This device cannot be forgotten while a ban is in place. Try again once it has lapsed.');
          forgetBtn.disabled = false;
          return;
        }
        // Storage is cleared either way; only the wording depends on whether
        // the server heard. An unheard key goes quiet and is swept in 90 days.
        toast(confirmed
          ? 'Forgotten. Lumi will not recognize this device again.'
          : 'Cleared on this device. Lumi will forget the rest within 90 days.');
        me = null;
        onKeepsake(null);
        render();
      },
    });
    const lines = [
      el('strong', { text: t('This device') }),
      el('p', {
        class: 'muted',
        // Which case the reader is in, because the answers are opposite and
        // the panel already knows. Regulars hang off the PERSON, which is the
        // account when there is one and only this device key otherwise -- so
        // the same sentence was reassuring for one reader and alarming for
        // another, and most alarming to exactly the people it did not apply
        // to. The 90 days is real: a keepsake unseen that long is revoked and
        // forgetPerson cascades through regulars, messages and memberships --
        // unless it was named, in which case it waits for its owner.
        text: account.loggedIn
          ? t('Lumi remembers you on this device with a random key, so rooms can find you again. It holds no name and no personal details, and it is deleted after 90 days away. Your regulars are kept with your account, not this device, so clearing it or being away does not lose them.')
          : name
            ? t('This device is signed in as your kept identity. Your regulars stay with the name for good, on any device you sign in from. Forgetting this device removes the name and the password with everything else.')
            : t('Lumi remembers you on this device with a random key. Your regulars are tied to it: clear it, or stay away 90 days, and they are gone.'),
      }),
    ];
    if (has && !account.loggedIn && me && !name) lines.push(keepOffer());
    lines.push(has
      ? el('div', { class: 'buy-row' }, [forgetBtn])
      : el('p', { class: 'muted', text: t('Nothing is remembered on this device yet.') }));
    return el('div', { class: 'keepsake-block' }, lines);
  }

  // "Keep this identity": a username and a password on the keepsake, so the
  // person can sign in to it on another device, or get it back after losing
  // this one. The identity stays the keepsake -- no account, nothing for sale
  // -- which is why it is offered to everyone, teens included.
  function keepOffer() {
    const u = el('input', { type: 'text', placeholder: t('Username'), maxlength: '24', autocomplete: 'username', aria: { label: 'Username' } });
    const p = el('input', { type: 'password', placeholder: t('Password (8+ characters)'), autocomplete: 'new-password', aria: { label: 'Password' } });
    const msg = el('div', { class: 'error' });
    const submit = el('button', {
      class: 'btn btn-primary',
      text: t('Keep it'),
      onClick: async () => {
        msg.textContent = '';
        if (!/^[a-z0-9_.-]{3,24}$/i.test(u.value.trim())) {
          msg.textContent = 'Pick a username (3-24 letters, digits, _ . -).';
          return;
        }
        if (p.value.length < 8) {
          msg.textContent = 'Password must be at least 8 characters.';
          return;
        }
        submit.disabled = true;
        const { status, data } = await nameKeepsake({ username: u.value.trim(), password: p.value, base: SERVER_HTTP });
        submit.disabled = false;
        if (status === 200) {
          // The recovery code, exactly once, as a step: an account's is the same.
          body.replaceChildren(renderRecoveryCode(data.recoveryCode, () => {
            toast(`Kept. You can sign in as @${data.username} on any device.`);
            onKeepsake(data);
            askMe();
            render();
          }, { what: 'this identity' }));
          return;
        }
        msg.textContent = data.error === 'username_taken'
          ? 'That username is taken.'
          : data.error === 'bad_username'
            ? 'Pick a username (3-24 letters, digits, _ . -).'
            : data.error === 'weak_password'
              ? 'Password must be at least 8 characters.'
              : status === 429 && data.message
                ? data.message
                : 'Could not keep it. Try again in a moment.';
      },
    });
    // Unfolded at once when the person came here to do exactly this.
    const form = el('div', { class: `auth-form auth-fields${opts.keep ? '' : ' hidden'}` }, [u, p, msg, submit]);
    if (opts.keep) queueMicrotask(() => u.focus());
    const openBtn = el('button', {
      class: 'btn btn-secondary btn-sm',
      hidden: opts.keep ? '' : null,
      text: t('Keep this identity'),
      onClick: () => {
        form.classList.remove('hidden');
        openBtn.hidden = true;
        u.focus();
      },
    });
    return el('div', { class: 'keepsake-name' }, [
      el('p', { class: 'muted', text: t('Add a username and password and your regulars stay with the name for good, on any device you sign in from.') }),
      openBtn,
      form,
    ]);
  }

  function renderDevice() {
    deviceBox.replaceChildren(keepsakeBlock());
  }

  function render() {
    body.replaceChildren();
    drawnName = keptName();
    if (!account.loggedIn) {
      const name = keptName();
      if (name) {
        // A kept identity is signed in the way an account is, as far as the
        // person can tell. What it is not -- an account -- is said plainly.
        body.append(
          el('div', { class: 'billing-account' }, [el('span', { text: `Signed in as @${name}` })]),
          el('p', { class: 'muted', text: t('Your regulars and messages follow this name to any device you sign in on.') }),
          // Where the name is signed in, and the way to end any of it.
          renderDevices(keepsakeDevicesApi(SERVER_HTTP), {
            onSignedOutHere: () => {
              me = null;
              onKeepsake(null);
              render();
            },
          }),
        );
      }
      // A teen who is signed in to their kept identity has nothing else to
      // sign in to; anyone else may still have an account.
      if (!name || band !== 'minor') {
        body.append(
          el('p', {
            class: 'muted',
            text: band === 'minor'
              ? t('Sign in with the name you kept and your regulars come with it.')
              : t('Signing in is instant. Creating an account is 18+ and includes a quick age check.'),
          }),
          // Defaults to the SIGN IN tab: someone opening this already has an
          // account far more often than not. The form's own tabs switch to
          // creating one. The band decides which doors the form has (auth.js).
          renderAuth(
            account,
            (acct, extra) => {
              if (extra && extra.keepsake) onKeepsake(extra.keepsake);
              else onChange(account.account);
              askMe();
              render();
            },
            { mode: opts.mode === 'register' ? 'register' : 'login', band },
          ),
        );
      }
    } else {
      body.append(
        el('div', { class: 'billing-account' }, [
          el('span', { text: `Signed in as ${account.account.username}` }),
          account.isPlus ? el('span', { class: 'plus-badge', text: 'PLUS' }) : null,
        ].filter(Boolean)),
        el('p', {
          class: 'muted',
          text: account.isPlus
            ? 'You are a Plus member. Thank you for keeping Lumi independent and ad-free.'
            : 'You are on the free plan, which is all of Lumi. Plus is optional extras, never a gate.',
        }),
        el('div', { class: 'buy-row' }, [
          el('button', {
            class: 'btn btn-secondary btn-sm',
            text: account.isPlus ? 'Manage Plus and extras' : 'See Lumi Plus',
            onClick: () => {
              dialog.close();
              opts.onPlus?.();
            },
          }),
          el('button', {
            class: 'btn btn-ghost btn-sm',
            text: t('Sign out'),
            onClick: async () => {
              await account.signOut();
              onChange(null);
              render();
            },
          }),
        ]),
        // Where the account is signed in, and the way to end any of it.
        renderDevices(accountDevicesApi(account), {
          onSignedOutHere: () => {
            onChange(null);
            render();
          },
        }),
      );
    }
    // Regulars are for everyone with an identity, account or keepsake, so the
    // door is here for both -- when the feature exists for this person at all.
    if (typeof opts.onRegulars === 'function') {
      body.append(
        el('div', { class: 'keepsake-block regulars-block' }, [
          el('strong', { text: t('Your regulars') }),
          el('p', { class: 'muted', text: t('People you have both waved at after a chat. You stay anonymous to each other.') }),
          el('div', { class: 'buy-row' }, [
            el('button', {
              class: 'btn btn-secondary btn-sm',
              text: opts.regularsCount ? `Regulars (${opts.regularsCount})` : 'Regulars',
              onClick: () => {
                dialog.close();
                opts.onRegulars();
              },
            }),
          ]),
        ]),
      );
    }
    // Below the account section for everyone: the device is remembered whether
    // or not a person ever signs in.
    renderDevice();
    body.append(deviceBox);
  }

  render();
  askMe();
  return modal;
}

export default { openAccount };
